import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import {
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import CaptchaModal from '../../components/modals/CaptchaModal';
import SuccessModal from '../../components/modals/SuccessModal';
import CaptchaStatusModal from '../../components/modals/CaptchaStatusModal';

import MobileAuthModal from '../../components/modals/MobileAuthModal';
import PasswordValidationPopup from '../../components/modals/PasswordValidationPopup';
import { useAuth } from '../../hooks/useAuth';
import { useGoogleOAuth } from '../../hooks/useGoogleOAuth';
import { useMicrosoftOAuth } from '../../hooks/useMicrosoftOAuth';
import { useFacebookOAuth } from '../../hooks/useFacebookOAuth';
import { useAppleOAuth } from '../../hooks/useAppleOAuth';
import OAuthLoadingOverlay from '../../components/OAuthLoadingOverlay';
import { validatePassword } from '../../utils/passwordUtils';
import { validateGoogleOAuthConfig } from '../../config/googleOAuth';
import { validateMicrosoftOAuthConfig } from '../../config/microsoftOAuth';
import { validateFacebookOAuthConfig } from '../../config/facebookOAuth';
import { getUserTimezone } from '../../utils/timezoneUtils';
// import AppleNameModal from '../../components/modals/AppleNameModal';
import { isPhoneEmpty, isValidE164, e164ToBackendFormat } from '../../utils/helperFunctions';
import { validateEmail } from '../../utils/emailValidation';
import PhoneNumberInput from '../../components/PhoneNumberInput';
import '../../styles/components/settings.css';

// Validation schema using yup
const registerSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .test('trimmed-length', 'First name must be at least 4 characters', value => !value || value.trim().length >= 4)
    .test('max-length', 'First name cannot exceed 30 characters', value => !value || value.length <= 30)
    .test('letters-only', 'First name can only contain letters and spaces', value => !value || /^[a-zA-Z\s]+$/.test(value))
    .test('not-only-spaces', 'First name cannot be only spaces', value => !value || value.trim().length > 0)
    .test('not-all-same', 'Enter a valid first name', value => !value || !/^([a-zA-Z])\1+$/.test(value.trim()))
    .test('no-repeated-chars', 'First name cannot have a character repeated more than twice in a row', value => !value || !/([a-zA-Z])\1{2,}/.test(value))
    .test('no-gibberish', 'Enter a valid first name', value => !value || !/[^aeiou\s]{5,}/i.test(value.trim())),
  lastName: yup
    .string()
    .test('min-length', 'Last name must be at least 2 characters', value => !value || value.length >= 2)
    .test('max-length', 'Last name cannot exceed 30 characters', value => !value || value.length <= 30)
    .test('letters-only', 'Last name can only contain letters', value => !value || /^[a-zA-Z]+$/.test(value))
    .test('not-all-same', 'Enter a valid last name', value => !value || !/^([a-zA-Z])\1+$/.test(value.trim()))
    .test('no-repeated-chars', 'Last name cannot have a character repeated more than twice in a row', value => !value || !/([a-zA-Z])\1{2,}/.test(value))
    .test('no-gibberish', 'Enter a valid last name', value => !value || !/[^aeiou\s]{5,}/i.test(value.trim())),
  userType: yup
    .number()
    .required('Please select a user type')
    .min(1, 'Please select a user type'),
  email: yup
    .string()
    .test('email-validation', '', function (value) {
      // Hard-block on Register: format + disposable. Typo suggestions are
      // surfaced via the `emailSuggestion` state below, not via this test.
      const result = validateEmail(value, { blockDisposable: true, checkTypo: false });
      if (result.ok) return true;
      return this.createError({ message: result.error });
    }),
  mobileNumber: yup
    .string()
    .test('required', 'Mobile number is required', value => !isPhoneEmpty(value))
    .test('valid', 'Please enter a valid mobile number for the selected country', value => isPhoneEmpty(value) || isValidE164(value)),
  password: yup
    .string(),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
  agreeToTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to the Terms of Service and Privacy Policy')
});

// Schema for social/mobile registration (only requires userType and agreeToTerms)
// const socialRegisterSchema = yup.object().shape({
//   userType: yup
//     .number()
//     .required('Please select a user type')
//     .min(1, 'Please select a user type'),
//   agreeToTerms: yup
//     .boolean()
//     .oneOf([true], 'Please agree to the Terms of Service and Privacy Policy')
// });

// Map backend registration errors to user-friendly modal title/description
const getRegistrationErrorDisplay = (error?: string): { title: string; description: string } => {
  const normalized = (error || '').toLowerCase();
  const alreadyExists = /already\s*(exists|registered|taken|in\s*use)/i.test(normalized);
  if (alreadyExists && /mobile|phone/.test(normalized)) {
    return {
      title: 'Mobile Number Already Registered',
      description: 'An account with this mobile number already exists. Please sign in or use a different number.'
    };
  }
  if (alreadyExists && /email/.test(normalized)) {
    return {
      title: 'Account Already Exists',
      description: 'An account with this email is already registered. Please sign in instead.'
    };
  }
  if (alreadyExists && /user\s*name|username/.test(normalized)) {
    return {
      title: 'Username Already Taken',
      description: 'This username is already in use. Please choose a different one.'
    };
  }
  return {
    title: 'Registration failed',
    description: error || 'Please try again'
  };
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { sendMobileOTP, verifyMobileOTP, register } = useAuth();
  const { googleLoginWithUserType, cancelOAuth: cancelGoogleOAuth } = useGoogleOAuth();
  const {
    microsoftLoginWithUserType,
    cancelOAuth: cancelMicrosoftOAuth,
    processRedirectResult: processMicrosoftRedirectResult,
  } = useMicrosoftOAuth();
  const {
    facebookLoginWithUserType,
    cancelOAuth: cancelFacebookOAuth,
    processRedirectResult: processFacebookRedirectResult,
  } = useFacebookOAuth();
  const {
    appleLoginWithUserType,
    cancelOAuth: cancelAppleOAuth,
    processRedirectResult: processAppleRedirectResult,
  } = useAppleOAuth();
  const [oauthInProgress, setOauthInProgress] = useState<null | 'Google' | 'Microsoft' | 'Facebook' | 'Apple'>(null);

  const handleCancelOAuth = () => {
    if (oauthInProgress === 'Google') {
      cancelGoogleOAuth();
      setIsGoogleLoading(false);
    } else if (oauthInProgress === 'Microsoft') {
      cancelMicrosoftOAuth();
      setIsMicrosoftLoading(false);
    } else if (oauthInProgress === 'Facebook') {
      cancelFacebookOAuth();
      setIsFacebookLoading(false);
    } else if (oauthInProgress === 'Apple') {
      cancelAppleOAuth();
      setIsAppleLoading(false);
    }
    setOauthInProgress(null);
  };

  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [showMobileAuth, setShowMobileAuth] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    userName: '',
    userType: '1',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    loginType: 'User Credentials',
    timezone: ''
  });
  
  // Modal states
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successAction, setSuccessAction] = useState('');

  // CAPTCHA status modal state
  const [showCaptchaStatus, setShowCaptchaStatus] = useState(false);
  const [captchaStatus, setCaptchaStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
  const [captchaStatusMessage, setCaptchaStatusMessage] = useState('');
  const [currentCaptchaCode, setCurrentCaptchaCode] = useState('');
  //Apple name modal state 
  // const [showAppleNameModal, setShowAppleNameModal] = useState(false);
  // Add state for success status
  const [successStatus, setSuccessStatus] = useState<'success' | 'failed'>('success');

  // Add state for password validation errors
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  
  // Add state for password validation popup
  const [showPasswordValidationPopup, setShowPasswordValidationPopup] = useState(false);

  // Add state for field validation errors
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  // Track if form is valid
  const [isFormValidState, setIsFormValidState] = useState(false);

  // Soft hint from mailcheck (e.g., "Did you mean user@gmail.com?"). Empty
  // string when there is no suggestion or the address is already invalid.
  const [emailSuggestion, setEmailSuggestion] = useState('');

  // State to show apple login in ios mobiles only
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  /* Check the device os ios or not */
  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ action: "GET_IS_IOS" })
      );

      const handler = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.action == "IS_IOS") {
            setIsIOSDevice(data.isIOS);
          }
        } catch (e) {
          console.error("Invalid message from RN:", e);
        }
      };

      // 🔥 React Native WebView sends messages through BOTH
      window.addEventListener("message", handler);
      // @ts-ignore - RN dispatches custom event
      document.addEventListener("message", handler);
      return () => {
        window.removeEventListener("message", handler);
        // @ts-ignore
        document.removeEventListener("message", handler);
      };
    } else {
      const ua = navigator.userAgent.toLowerCase();
      const platform = navigator.platform;

      if (platform.includes("mac") || ua.includes("mac")) {
        setIsIOSDevice(true);
        return;
      };
    }
  }, [])

  // Detect user timezone on component mount
  useEffect(() => {
    const detectedTimezone = getUserTimezone();
    setFormData(prev => ({
      ...prev,
      timezone: detectedTimezone
    }));
  }, []);

  // iOS browsers and the iOS RN WebView redirect through the OAuth provider
  // in the same window (popup OAuth is broken there). On return, each hook
  // has stashed its result in sessionStorage — drain them on mount and
  // complete the sign-in.
  useEffect(() => {
    const appleHandled = processAppleRedirectResult({
      onSuccess: (message) => {
        setIsAppleLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Apple Login Successful`);
        setSuccessAction(`${message}. Redirecting to devices...`);
        setSuccessStatus('success');
        setShowSuccess(true);
        localStorage.setItem('apple-login-used', 'true');
        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      onError: (error) => {
        setIsAppleLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Apple Login Failed`);
        setSuccessAction(error || 'Please try again');
        setShowSuccess(true);
        setSuccessStatus('failed');
      },
    });
    if (appleHandled) {
      setIsAppleLoading(true);
      setOauthInProgress('Apple');
    }

    const microsoftHandled = processMicrosoftRedirectResult({
      onSuccess: (message) => {
        setIsMicrosoftLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Microsoft Login Successful`);
        setSuccessAction(`${message}. Redirecting to devices...`);
        setSuccessStatus('success');
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      onError: (error) => {
        setIsMicrosoftLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Microsoft Login Failed`);
        setSuccessAction(error || 'Please try again');
        setShowSuccess(true);
        setSuccessStatus('failed');
      },
    });
    if (microsoftHandled) {
      setIsMicrosoftLoading(true);
      setOauthInProgress('Microsoft');
    }

    const facebookHandled = processFacebookRedirectResult({
      onSuccess: (message) => {
        setIsFacebookLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Facebook Login Successful`);
        setSuccessAction(`${message}. Redirecting to devices...`);
        setSuccessStatus('success');
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      onError: (error) => {
        setIsFacebookLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Facebook Login Failed`);
        setSuccessAction(error || 'Please try again');
        setShowSuccess(true);
        setSuccessStatus('failed');
      },
    });
    if (facebookHandled) {
      setIsFacebookLoading(true);
      setOauthInProgress('Facebook');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear confirmPassword error when passwords match
  useEffect(() => {
    if (formData.password && formData.confirmPassword && formData.password === formData.confirmPassword) {
      if (fieldErrors.confirmPassword) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
    }
  }, [formData.password, formData.confirmPassword, fieldErrors.confirmPassword]);

  // Check form validity whenever form data changes
  useEffect(() => {
    const checkValidity = async () => {
      const passwordValidationErrors = validatePassword(formData.password);
      if (passwordValidationErrors.length > 0) {
        setIsFormValidState(false);
        return;
      }
      
      try {
        await registerSchema.validate(formData, { abortEarly: false });
        setIsFormValidState(true);
      } catch (error) {
        setIsFormValidState(false);
      }
    };
    
    checkValidity();
  }, [formData]);

  // Function to clear form data
  const clearFormData = () => {
    const detectedTimezone = getUserTimezone();
    setFormData({
      firstName: '',
      lastName: '',
      userName: '0',
      userType: '1', // Default to Individual
      email: '',
      mobileNumber: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
      loginType: 'User Credentials',
      timezone: detectedTimezone
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPasswordErrors([]);
    setFieldErrors({});
    setShowPasswordValidationPopup(false);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Special handling for mobile number to ensure
    const processedValue = type === 'checkbox' ? checked : value;
    
    // Update form data first
    const updatedFormData = {
      ...formData,
      [name]: processedValue
    };
    
    setFormData(updatedFormData);

    // Validate password when password field changes
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
      // Show popup automatically when there are errors
      setShowPasswordValidationPopup(errors.length > 0);
      if(errors.length > 0) {
        setFieldErrors(prev => ({
          ...prev,
          password: errors.length === 5 ? 'Password is required' : 'Password does not meet requirements',
        }));
      } else {
        setFieldErrors(prev => ({
          ...prev,
          password: '',
        }));
      }
      
      // Also re-validate confirmPassword when password changes
      if (formData.confirmPassword) {
        try {
          await registerSchema.validateAt('confirmPassword', { 
            password: value, 
            confirmPassword: formData.confirmPassword 
          });
          // Clear confirmPassword error if validation passes
          if (fieldErrors.confirmPassword) {
            setFieldErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.confirmPassword;
              return newErrors;
            });
          }
        } catch (validationError) {
          setFieldErrors(prev => ({ ...prev, confirmPassword: (validationError as Error).message }));
        }
      }
    }

    if(name !== 'password') {
      try {
        // For confirmPassword, validate against the updated form data to check password match
        if (name === 'confirmPassword') {
          await registerSchema.validateAt(name, updatedFormData);
        } else {
          await registerSchema.validateAt(name, { [name]: type === 'checkbox' ? checked : value });
        }
        // Clear field error when field validation passes
        if (fieldErrors[name]) {
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }
      } catch (validationError) {
        setFieldErrors(prev => ({ ...prev, [name]: (validationError as Error).message }));
      }
    }

    // Email typo hint runs separately from the yup test (which only enforces
    // hard rules). Only show a suggestion when format/disposable have passed.
    if (name === 'email') {
      const typoCheck = validateEmail(value as string, { blockDisposable: false, checkTypo: true });
      setEmailSuggestion(typoCheck.ok && typoCheck.suggestion ? typoCheck.suggestion : '');
    }
  };

  // Apply a mailcheck suggestion: write it into the form, clear the hint,
  // and rerun validation so the field-level state catches up.
  const acceptEmailSuggestion = async () => {
    if (!emailSuggestion) return;
    const next = emailSuggestion;
    setFormData(prev => ({ ...prev, email: next }));
    setEmailSuggestion('');
    try {
      await registerSchema.validateAt('email', { email: next });
      setFieldErrors(prev => {
        if (!prev.email) return prev;
        const copy = { ...prev };
        delete copy.email;
        return copy;
      });
    } catch (validationError) {
      setFieldErrors(prev => ({ ...prev, email: (validationError as Error).message }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let hasErrors = false;
    const passwordValidationErrors = validatePassword(formData.password);
    
    try {
      // Additional password validation using existing utility
      if (passwordValidationErrors.length > 0) {
        setPasswordErrors(passwordValidationErrors);
        // Show password validation popup when validation fails on submit
        setShowPasswordValidationPopup(true);

        setFieldErrors(prev => ({
          ...prev,
          password: passwordValidationErrors.length === 5 ? 'Password is required' : 'Password does not meet requirements',
        }));
        hasErrors = true;
      }

      // Validate all fields using yup schema
      await registerSchema.validate(formData, { abortEarly: false });

      if (hasErrors) {
        return;
      }

      // Clear all errors if validation passes
      setFieldErrors({});

      setIsLoading(true);
      // Simulate API call - show CAPTCHA after registration attempt
      // setTimeout(() => {
      //   setShowCaptcha(true);
      //   setIsLoading(false);
      // }, 1000);
      // Call register function after CAPTCHA verification
      const registerPayload = {
        username: formData.email.split('@')[0],
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        firstName: formData.firstName,
        lastName: formData.lastName,
        userType: Number(formData.userType),
        mobileNumber: e164ToBackendFormat(formData.mobileNumber),
        loginType: formData.loginType,
        timezone: formData.timezone,
        // AQI standard is optional during registration - user can set it later in settings
        // aqiStandardId: undefined
      };
      
      try {
        const result = await register(registerPayload);
        setTimeout(() => {
          setIsLoading(false);
          if (result.success) {
            setSuccessMessage('Account created successfully');
            setSuccessAction('Proceed to Login');
            setSuccessStatus('success');
            setShowSuccess(true);
            // Clear form data after successful registration
            clearFormData();
          } else {
            // Handle specific error messages
            let errorMessage = result.error || 'Please try again';
            if (errorMessage.includes('AQI standard') || errorMessage.includes('configuration')) {
              errorMessage = 'AQI standard configuration issue. Please try again or contact support.';
            }
            const display = getRegistrationErrorDisplay(errorMessage);
            setSuccessMessage(display.title);
            setSuccessAction(display.description);
            setSuccessStatus('failed');
            setShowSuccess(true);
          }
        }, 2000);
      } catch (error) {
        setTimeout(() => {
          setIsLoading(false);
          // Handle specific error messages
          let errorMessage = 'An unexpected error occurred';
          if (error instanceof Error) {
            if (error.message.includes('AQI standard') || error.message.includes('configuration')) {
              errorMessage = 'AQI standard configuration issue. Please try again or contact support.';
            } else {
              errorMessage = error.message;
            }
          }
          const display = getRegistrationErrorDisplay(errorMessage);
          setSuccessMessage(display.title);
          setSuccessAction(display.description);
          setSuccessStatus('failed');
          setShowSuccess(true);
        }, 2000);
      }
    } catch (validationError) {
      if (validationError instanceof yup.ValidationError) {
        const errors: {[key: string]: string} = {};
        validationError.inner.forEach((error) => {
          if (error.path) {
            errors[error.path] = error.message;
          }
        });
        setFieldErrors(prev => ({ ...prev, ...errors }));
        hasErrors = true;
      }
    }
  };

  const handleSocialRegister = async (loginType: string) => {

    // Handle Gmail registration with real Google OAuth
    if (loginType.toLowerCase() === 'gmail') {
      try {
        // Check if Google OAuth is properly configured
        if (!validateGoogleOAuthConfig()) {
          setSuccessMessage(`${loginType} Registration Failed`);
          setSuccessAction('Google OAuth is not configured. Please contact administrator.');
          setSuccessStatus('failed');
          setShowSuccess(true);
          return;
        }

        setIsGoogleLoading(true);
        setOauthInProgress('Google');
        const googleLogin = googleLoginWithUserType(
          1,
          // Success callback - called when backend returns success
          (backendMessage?: string) => {
            setIsGoogleLoading(false);
            setOauthInProgress(null);
            setSuccessMessage(`${loginType} Registration Successful`);
            // Use backend message if available, otherwise use default
            // Backend can send messages like: "Welcome! Your account has been created successfully."
            setSuccessAction(`${backendMessage} Redirecting to devices...`);
            setSuccessStatus('success');
            setShowSuccess(true);

            // Navigate to devices after a short delay
            setTimeout(() => {
              navigate('/devices');
            }, 1000);
          },
          // Error callback - called when backend returns error with actual backend message
          (backendError: string) => {
            setIsGoogleLoading(false);
            setOauthInProgress(null);
            setSuccessMessage(`${loginType} Registration Failed`);
            // Use the actual backend error message
            // Backend can send messages like: "Email already exists", "Invalid user type", etc.
            setSuccessAction(backendError || 'An unexpected error occurred');
            setSuccessStatus('failed');
            setShowSuccess(true);
          }
        );
        googleLogin();
      } catch (error) {
        setIsGoogleLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`${loginType} Registration Failed`);
        setSuccessAction(error instanceof Error ? error.message : 'An unexpected error occurred');
        setSuccessStatus('failed');
        setShowSuccess(true);
      }
      return;
    }

    // Handle Microsoft registration with real Microsoft OAuth
    if (loginType.toLowerCase() === 'microsoft') {
      try {
        // Check if Microsoft OAuth is properly configured
        if (!validateMicrosoftOAuthConfig()) {
          setSuccessMessage(`${loginType} Registration Failed`);
          setSuccessAction('Microsoft OAuth is not configured. Please contact administrator.');
          setSuccessStatus('failed');
          setShowSuccess(true);
          return;
        }

        setIsMicrosoftLoading(true);
        setOauthInProgress('Microsoft');
        const microsoftLogin = microsoftLoginWithUserType(
          1, // userType
          // Success callback - called when backend returns success
          (backendMessage?: string) => {
            setIsMicrosoftLoading(false);
            setOauthInProgress(null);
            setSuccessMessage(`${loginType} Registration Successful`);
            // Use backend message if available, otherwise use default
            setSuccessAction(`${backendMessage} Redirecting to devices...`);
            setSuccessStatus('success');
            setShowSuccess(true);

            // Navigate to devices after a short delay
            setTimeout(() => {
              navigate('/devices');
            }, 1000);
          },
          // Error callback - called when backend returns error with actual backend message
          (backendError: string) => {
            setIsMicrosoftLoading(false);
            setOauthInProgress(null);
            setSuccessMessage(`${loginType} Registration Failed`);
            // Use the actual backend error message
            setSuccessAction(backendError || 'An unexpected error occurred');
            setSuccessStatus('failed');
            setShowSuccess(true);
          }
        );
        microsoftLogin();
      } catch (error) {
        setIsMicrosoftLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`${loginType} Registration Failed`);
        setSuccessAction(error instanceof Error ? error.message : 'An unexpected error occurred');
        setSuccessStatus('failed');
        setShowSuccess(true);
      }
      return;
    }

    // Handle Facebook registration with real Facebook OAuth
    if (loginType.toLowerCase() === 'facebook') {
      try {
        // Check if Facebook OAuth is properly configured
        if (!validateFacebookOAuthConfig()) {
          setSuccessMessage(`${loginType} Registration Failed`);
          setSuccessAction('Facebook OAuth is not configured. Please contact administrator.');
          setSuccessStatus('failed');
          setShowSuccess(true);
          return;
        }

        setIsFacebookLoading(true);
        setOauthInProgress('Facebook');
        const facebookLogin = facebookLoginWithUserType(
          1, // userType
          // Success callback - called when backend returns success
          (backendMessage?: string) => {
            setIsFacebookLoading(false);
            setOauthInProgress(null);
            setSuccessMessage(`${loginType} Registration Successful`);
            // Use backend message if available, otherwise use default
            setSuccessAction(`${backendMessage} Redirecting to devices...`);
            setSuccessStatus('success');
            setShowSuccess(true);

            // Navigate to devices after a short delay
            setTimeout(() => {
              navigate('/devices');
            }, 1000);
          },
          // Error callback - called when backend returns error with actual backend message
          (backendError: string) => {
            setIsFacebookLoading(false);
            setOauthInProgress(null);
            setSuccessMessage(`${loginType} Registration Failed`);
            // Use the actual backend error message
            setSuccessAction(backendError || 'An unexpected error occurred');
            setSuccessStatus('failed');
            setShowSuccess(true);
          }
        );
        facebookLogin();
      } catch (error) {
        setIsFacebookLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`${loginType} Registration Failed`);
        setSuccessAction(error instanceof Error ? error.message : 'An unexpected error occurred');
        setSuccessStatus('failed');
        setShowSuccess(true);
      }
      return;
    }

    if(loginType.toLowerCase() === 'form'){
      setIsSmallScreen(true);
    }

    // Handle other loginTypes with dummy data for now
    // try {
    //   const socialData = {
    //     loginType: loginType.toLowerCase() as 'google' | 'microsoft' | 'uaepass',
    //     accessToken: `dummy_${loginType.toLowerCase()}_token_${Date.now()}`,
    //     userData: {
    //       email: formData.email || `user@${loginType.toLowerCase()}.com`,
    //       name: `${formData.firstName} ${formData.lastName}`.trim() || `${loginType} User`,
    //       userType: formData.userType || '1', // Default to Individual if not selected
    //       picture: undefined
    //     }
    //   };

    //   const result = await socialLogin(socialData);
      
    //   if (result.success) {
    //     setSuccessMessage(`${loginType} Registration Successful`);
    //     setSuccessAction('Account created successfully');
    //     setShowSuccess(true);
    //   } else {
    //     setSuccessMessage(`${loginType} Registration Failed`);
    //     setSuccessAction(result.error || 'Please try again');
    //     setShowSuccess(true);
    //   }
    // } catch (error) {
    //   setSuccessMessage(`${loginType} Registration Failed`);
    //   setSuccessAction('An unexpected error occurred');
    //   setShowSuccess(true);
    // }
  };

   // Handle apple login
  // const handleAppleLoginClick = () => {
  //   const appleLoginUsed = localStorage.getItem("apple-login-used");
  //   if (appleLoginUsed == "true") {
  //     handleAppleLogin("", "");
  //   } else {
  //     setShowAppleNameModal(true);
  //   }
  // }; 

  const handleAppleLogin = () => {
    setIsAppleLoading(true);
    setOauthInProgress('Apple');
    // setShowAppleNameModal(false);
    // Handle Apple OAuth login
    const appleLogin = appleLoginWithUserType(
      1, // userType
      // firstName,
      // lastName,
      (message) => {
        setIsAppleLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Apple Login Successful`);
        setSuccessAction(`${message}. Redirecting to devices...`);
        setSuccessStatus('success');
        setShowSuccess(true);
        localStorage.setItem("apple-login-used", "true");

        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      (error) => {
        setIsAppleLoading(false);
        setOauthInProgress(null);
        setSuccessMessage(`Apple Login Failed`);
        setSuccessAction(error || 'Please try again');
        setShowSuccess(true);
        setSuccessStatus('failed');
      }
    );
    appleLogin();
    return;
  }

  const handleFormRegisterClose = () => {
    setIsSmallScreen(false);
  };

  // const handleMobileRegister = () => {
  //   alert('Mobile registration implementation is on hold');
  //   return;
  //   // setShowMobileAuth(true);
  // };

  // Mobile authentication handlers
  const handleMobileAuthClose = () => {
    setShowMobileAuth(false);
  };



  const handleSendOTP = async (mobileNumber: string) => {
    try {
      const result = await sendMobileOTP(mobileNumber);
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to send OTP' };
    }
  };

  const handleVerifyOTP = async (mobileNumber: string, otp: string) => {
    try {
      const result = await verifyMobileOTP({ mobileNumber, otp });
      
      if (result.success) {
        setSuccessMessage('Mobile Registration Successful');
        setSuccessAction('Account created successfully. Redirecting to devices...');
        setShowSuccess(true);
        setShowMobileAuth(false);
        setSuccessStatus('success');
        // Navigate to devices after a short delay
        setTimeout(() => {
          navigate('/devices'); 
        }, 1500);
      }
      
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to verify OTP' };
    }
  };

  // CAPTCHA handlers
  const handleCaptchaVerify = async (code: string) => {
    // Show verifying status
    setCaptchaStatus('verifying');
    setCaptchaStatusMessage('Verifying your CAPTCHA response...');
    setShowCaptchaStatus(true);
    setShowCaptcha(false);

    if (code === currentCaptchaCode) {
      // Show success status
      setCaptchaStatus('success');
      setCaptchaStatusMessage('CAPTCHA verification successful! Creating your account...');

      // Call register function after CAPTCHA verification
      const registerPayload = {
        username: formData.email.split('@')[0],
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        firstName: formData.firstName,
        lastName: formData.lastName,
        userType: Number(formData.userType),
        mobileNumber: formData.mobileNumber,
        loginType: formData.loginType
      };
      try {
        const result = await register(registerPayload);
        setTimeout(() => {
          setShowCaptchaStatus(false);
          if (result.success) {
            setSuccessMessage('Account created successfully');
            setSuccessAction('Proceed to Login');
            setSuccessStatus('success');
            setShowSuccess(true);
            // Clear form data after successful registration
            clearFormData();
          } else {
            const display = getRegistrationErrorDisplay(result.error);
            setSuccessMessage(display.title);
            setSuccessAction(display.description);
            setSuccessStatus('failed');
            setShowSuccess(true);
          }
        }, 2000);
      } catch (error) {
        setTimeout(() => {
          setShowCaptchaStatus(false);
          const display = getRegistrationErrorDisplay(
            error instanceof Error ? error.message : undefined
          );
          setSuccessMessage(display.title);
          setSuccessAction(display.description);
          setSuccessStatus('failed');
          setShowSuccess(true);
        }, 2000);
      }
    } else {
      // Show failed status
      setCaptchaStatus('failed');
      setCaptchaStatusMessage('Incorrect CAPTCHA code. Please try again.');
      setCaptchaError(true);
    }
  };

  const handleCaptchaClose = () => {
    setShowCaptcha(false);
    setCaptchaError(false);
  };

  const handleCaptchaErrorClose = () => {
    setCaptchaError(false);
  };

  // Success modal handlers
  const handleSuccessClose = () => {
    setShowSuccess(false);
  };

  const handleSuccessAction = () => {
    setShowSuccess(false);
    if(successStatus === 'success'){
      navigate('/login');
    }
    // In real app, this would navigate to login
  };

  // E.164 phone number changes flow through the shared input component;
  // route them into the same form state used by other fields. Skip inline
  // validation while the input only contains a dial code (the library emits
  // "+971" on mount before any user input) — the submit handler runs full
  // validation, so the required check still gates form submission.
  const handleMobileNumberChange = async (value: string) => {
    setFormData(prev => ({ ...prev, mobileNumber: value }));
    if (isPhoneEmpty(value)) {
      setFieldErrors(prev => {
        if (!prev.mobileNumber) return prev;
        const next = { ...prev };
        delete next.mobileNumber;
        return next;
      });
      return;
    }
    try {
      await registerSchema.validateAt('mobileNumber', { mobileNumber: value });
      setFieldErrors(prev => {
        if (!prev.mobileNumber) return prev;
        const next = { ...prev };
        delete next.mobileNumber;
        return next;
      });
    } catch (validationError) {
      setFieldErrors(prev => ({ ...prev, mobileNumber: (validationError as Error).message }));
    }
  };


  return (
    <div className="register-container">
      {/*to show dark shade background */}
      <div className='register-background-conatiner'></div>
      {/*Main Header */}
      <div className="register-header-top">
        <div className="logo-container h-[70px]">
          {/* Header */}
          <a href="https://smarteco.ai/" target="_blank" rel="noopener noreferrer"className="cursor-pointer hover:opacity-80 transition-opacity inline-block h-full">
              <img 
                src={`${process.env.PUBLIC_URL}/SVGIcons/NewSmartEcoLogo.svg`}
                alt="Smart Eco Logo" 
                className="h-full w-full object-contain"
              /> 
          </a>

        </div>
      </div>

      {/* Main Content */}
      <div
        className={`register-main-content zooomLevelForReportsPage ${isSmallScreen ? 'small-screen' : ''}`}
      >
        {/* Header for mobile and tablet */}
        <div className="register-mobile-header">
          <div className="logo-container mb-5 h-[70px]">
            <a href="https://smarteco.ai/" target="_blank" rel="noopener noreferrer"className="cursor-pointer hover:opacity-80 transition-opacity h-full">
              <img 
                src={`${process.env.PUBLIC_URL}/SVGIcons/NewSmartEcoLogo.svg`}
                alt="Smart Eco Logo" 
                className="h-full w-full object-contain"
              /> 
            </a>
          </div>
          <h2 className="register-section-title">Create Your Account</h2>
          <p className="register-section-subtitle">
          Choose your preferred registration method.
          </p>
        </div>
        
        {/* Left Section - Social Registration */}
        <div className="register-left-section">
          {/* Header */}
          <div className="register-left-section-header">
            <h2 className="register-section-title">Create Your Account</h2>
            <p className="register-section-subtitle">
            Choose your preferred registration method.
            </p>
          </div>

          {/* Social Registration Methods */}
          <div className="register-social-methods">
            {/* Register with form */}
            <button
              onClick={() => handleSocialRegister('Form')}
              disabled={isGoogleLoading}
              className="register-social-btn with-form-btn"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 10C14.2091 10 16 8.20914 16 6C16 3.79086 14.2091 2 12 2C9.79086 2 8 3.79086 8 6C8 8.20914 9.79086 10 12 10Z"
                  fill="#217C70"
                />
                <path
                  d="M20 17.5C20 19.985 20 22 12 22C4 22 4 19.985 4 17.5C4 15.015 7.582 13 12 13C16.418 13 20 15.015 20 17.5Z"
                  fill="#217C70"
                />
              </svg>
              Register with Form
            </button>

            {/* Mobile Registration 
            <button
              onClick={handleMobileRegister}
              className="register-social-btn md:hidden block"
            >
              <svg width="14" height="22" viewBox="0 0 14 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.4375 0.453125H1.5625C0.735156 0.453125 0.0625 1.12578 0.0625 1.95312V19.9531C0.0625 20.7805 0.735156 21.4531 1.5625 21.4531H12.4375C13.2648 21.4531 13.9375 20.7805 13.9375 19.9531V1.95312C13.9375 1.12578 13.2648 0.453125 12.4375 0.453125ZM7 18.3125C6.48203 18.3125 6.0625 17.893 6.0625 17.375C6.0625 16.857 6.48203 16.4375 7 16.4375C7.51797 16.4375 7.9375 16.857 7.9375 17.375C7.9375 17.893 7.51797 18.3125 7 18.3125Z" fill="#00C8E6"/>
              </svg>

              Register with mobile number
            </button> */}

            {/* Divider */}
            <div className="register-divider mobile">OR</div>

            <button
              onClick={() => handleSocialRegister('Gmail')}
              disabled={isGoogleLoading}
              className="register-social-btn"
            >
              {isGoogleLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--gradient-button-left-color)] mr-2"></div>
                  Connecting...
                </div>
              ) : (
                <>
                  <svg
                    width="24"
                    height="18"
                    viewBox="0 0 24 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.75 0H21L12 7.107L3 0H2.25C1.008 0 0 1.008 0 2.25V15.75C0 16.992 1.008 18 2.25 18H3V3.969L12 10.8915L21 3.9675V18H21.75C22.992 18 24 16.992 24 15.75V2.25C24 1.008 22.992 0 21.75 0Z"
                      fill="#F44336"
                    />
                  </svg>
                  Continue with Gmail
                </>
              )}
            </button>

            <button
              onClick={() => handleSocialRegister('Microsoft')}
              disabled={isMicrosoftLoading}
              className="register-social-btn"
            >
              {isMicrosoftLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--gradient-button-left-color)] mr-2"></div>
                  Connecting...
                </div>
              ) : (
                <>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_423_1037)">
                      <path
                        d="M12.75 11.25H24V0.75C24 0.336 23.664 0 23.25 0H12.75V11.25Z"
                        fill="#4CAF50"
                      />
                      <path
                        d="M11.25 11.25V0H0.75C0.336 0 0 0.336 0 0.75V11.25H11.25Z"
                        fill="#F44336"
                      />
                      <path
                        d="M11.25 12.75H0V23.25C0 23.664 0.336 24 0.75 24H11.25V12.75Z"
                        fill="#2196F3"
                      />
                      <path
                        d="M12.75 12.75V24H23.25C23.664 24 24 23.664 24 23.25V12.75H12.75Z"
                        fill="#FFC107"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_423_1037">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  Continue with Microsoft
                </>
              )}
            </button>

            <button
              onClick={() => handleSocialRegister('facebook')}
              className="register-social-btn"
            >
              {isFacebookLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--gradient-button-left-color)] mr-2"></div>
                  Connecting...
                </div>
              ) : (
                <>
                  <svg
                    width="12"
                    height="22"
                    viewBox="0 0 12 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.53598 12.3421H0.993873C0.583347 12.3421 0.457031 12.1842 0.457031 11.8052C0.457031 10.7789 0.457031 9.72103 0.457031 8.69471C0.457031 8.28419 0.614926 8.12629 0.993873 8.12629H3.53598V5.8684C3.5044 4.87366 3.74124 3.86313 4.23072 2.96313C4.75177 2.06313 5.57282 1.39997 6.52019 1.0684C7.16756 0.831553 7.79914 0.736816 8.47808 0.736816H10.9886C11.3518 0.736816 11.5097 0.894711 11.5097 1.25787V4.17892C11.5097 4.54208 11.3518 4.69997 10.9886 4.69997C10.2939 4.69997 9.59914 4.69997 8.9044 4.73155C8.20966 4.76313 7.8465 5.06313 7.8465 5.78945C7.81493 6.56313 7.8465 7.30524 7.8465 8.09471H10.7991C11.2097 8.09471 11.3676 8.25261 11.3676 8.66313V11.7737C11.3676 12.1842 11.2412 12.3421 10.7991 12.3421H7.87808V20.6947C7.87808 21.1368 7.72019 21.2789 7.29387 21.2789H4.08861C3.70966 21.2789 3.55177 21.121 3.55177 20.7421V12.3421H3.53598Z"
                      fill="url(#paint0_linear_585_771)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_585_771"
                        x1="1.77577"
                        y1="5.23997"
                        x2="12.7188"
                        y2="16.1829"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#42A5F5" />
                        <stop offset="1" stopColor="#1565C0" />
                      </linearGradient>
                    </defs>
                  </svg>
                  Continue with Facebook
                </>
              )}
            </button>

            {isIOSDevice && (
              <button
                onClick={handleAppleLogin}
                className="register-social-btn"
              >
                {isAppleLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--gradient-button-left-color)] mr-2"></div>
                    Connecting...
                  </div>
                ) : (
                  <>
                    <svg fill="#000000" width="22px" height="24px" viewBox="0 0 22 24" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"></path> </g></svg>
                    Continue with Apple
                  </>
                )}
              </button>
            )}

            {/* Divider */}
            <div className="register-divider">OR</div>

            {/* Mobile Registration 
            <button
              onClick={handleMobileRegister}
              className="register-social-btn"
            >
              <svg width="14" height="22" viewBox="0 0 14 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.4375 0.453125H1.5625C0.735156 0.453125 0.0625 1.12578 0.0625 1.95312V19.9531C0.0625 20.7805 0.735156 21.4531 1.5625 21.4531H12.4375C13.2648 21.4531 13.9375 20.7805 13.9375 19.9531V1.95312C13.9375 1.12578 13.2648 0.453125 12.4375 0.453125ZM7 18.3125C6.48203 18.3125 6.0625 17.893 6.0625 17.375C6.0625 16.857 6.48203 16.4375 7 16.4375C7.51797 16.4375 7.9375 16.857 7.9375 17.375C7.9375 17.893 7.51797 18.3125 7 18.3125Z" fill="#00C8E6"/>
              </svg>

              Register with mobile number
            </button> */}
          </div>

          <div className="register-footer">
            <p className="register-footer-text">
              Already have an account?{' '}
              <Link to="/login" className="register-footer-link">
                Sign In
              </Link>
            </p>
          </div>
          
          {/* Copy Right message - Desktop only */}
          <div className='register-copy-right-message-desktop copy-right-message'>© 2026 iSpatial Techno Solutions, All Rights Reserved</div>
        </div>

        {/* Copy Right message - mobile only */}
        <div className='register-copy-right-message-mobile copy-right-message'>© 2026 iSpatial Techno Solutions, All Rights Reserved</div>

        <div className="register-divider-vertical"></div>
        {/* Form Section for desktop */}
        <div className="register-right-section">
          <h2 className="register-form-title">Register with Email</h2>

          <form noValidate onSubmit={handleSubmit}>
            {/* Name Fields */}
            <div className="register-form-row">
              <div className="register-form-field">
                <label className="register-field-label">
                  First Name <span className="text-red-500">*</span>
                </label>
                <div className="register-input-with-icon">
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/solar_user-bold1.svg`}
                    alt="User"
                    className="register-input-icon"
                  />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    maxLength={30}
                    className={`register-input shadow-sm ${fieldErrors.firstName ? 'input-error-border' : ''}`}
                    placeholder="Enter first name"
                  />
                </div>
                {fieldErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {fieldErrors.firstName}
                  </p>
                )}
              </div>

              <div className="register-form-field">
                <label className="register-field-label">Last Name</label>
                <div className="register-input-with-icon">
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/solar_user-bold1.svg`}
                    alt="User"
                    className="register-input-icon"
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    maxLength={30}
                    className={`register-input shadow-sm ${fieldErrors.lastName ? 'input-error-border' : ''}`}
                    placeholder="Enter last name"
                  />
                </div>
                {fieldErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {fieldErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Email and Mobile Number */}
            <div className="register-form-row">
              <div className="register-form-field">
                <label className="register-field-label">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="register-input-with-icon">
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/Email1.svg`}
                    alt="Mail"
                    className="register-input-icon"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`register-input shadow-sm ${fieldErrors.email ? 'input-error-border' : ''}`}
                    placeholder="Enter your email"
                    onKeyDown={(e) => {
                      if (e.key.length === 1 && !/[a-zA-Z0-9@._-]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                      }
                    }} 
                  />
                </div>
                {fieldErrors.email && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {fieldErrors.email}
                  </p>
                )}
                {!fieldErrors.email && emailSuggestion && (
                  <p className="text-sm mt-1 text-white">
                    Did you mean{' '}
                    <button
                      type="button"
                      onClick={acceptEmailSuggestion}
                      className="font-semibold underline text-[#22c55e] hover:opacity-80"
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                )}
              </div>

              <div className="register-form-field">
                <label className="register-field-label" htmlFor="register-mobile-number">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <PhoneNumberInput
                  inputId="register-mobile-number"
                  inputName="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleMobileNumberChange}
                  defaultCountry="ae"
                  placeholder="Enter mobile number"
                  error={fieldErrors.mobileNumber}
                />
              </div>
            </div>

            {/* Password Fields */}
            <div className="register-form-row">
              <div className="register-form-field">
                <label className="register-field-label">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="register-input-with-icon">
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled1.svg`}
                    alt="Lock"
                    className="register-input-icon"
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={() => {
                      // Hide popup when password field loses focus
                      setShowPasswordValidationPopup(false);
                    }}
                    className={`register-input shadow-sm ${fieldErrors.password ? 'input-error-border' : ''}`}
                    placeholder="Create your password"
                  />
                  <button
                    type="button"
                    className="register-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#FFFFFF"/>
                      </svg>
                    ) : (
                      <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#FFFFFF"/>
                      </svg>
                    )}
                  </button>
                </div>
                <div className="password-validation-popup-container">
                  <PasswordValidationPopup
                    isVisible={showPasswordValidationPopup}
                    errors={passwordErrors}
                    // password={formData.password}
                    onClose={() => setShowPasswordValidationPopup(false)}
                  />
                </div>
                {fieldErrors.password && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {fieldErrors.password}
                  </p>
                )}
              </div>

              <div className="register-form-field">
                <label className="register-field-label">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="register-input-with-icon">
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled1.svg`}
                    alt="Lock"
                    className="register-input-icon"
                  />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`register-input shadow-sm ${fieldErrors.confirmPassword ? 'input-error-border' : ''}`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="register-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#FFFFFF"/>
                      </svg>
                    ) : (
                      <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#FFFFFF"/>
                      </svg>                      
                    )}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Terms and Privacy Policy */}
            <div className="register-terms-section">
              <div
                className={`register-terms-container ${fieldErrors.agreeToTerms && 'input-error-border'}`}
              >
                <label className="register-terms-checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    className="register-terms-checkbox"
                  />
                  <span className='checkmark'></span>
                  <span className="register-terms-text">
                    I agree to the{' '}
                    <Link 
                      to="/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="register-terms-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link 
                      to="/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="register-terms-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </label>
              </div>
              {fieldErrors.agreeToTerms && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.agreeToTerms}
                </p>
              )}
            </div>

            {/* Create Account Button */}
            <div
              onClick={async (e) => {
                // If form is invalid, show errors
                if (!isFormValidState || !formData.agreeToTerms) {
                  e.preventDefault();
                  e.stopPropagation();
                  // Trigger validation to show all errors
                  try {
                    await registerSchema.validate(formData, { abortEarly: false });
                  } catch (validationError) {
                    if (validationError instanceof yup.ValidationError) {
                      const errors: {[key: string]: string} = {};
                      validationError.inner.forEach((error) => {
                        if (error.path) {
                          errors[error.path] = error.message;
                        }
                      });
                      setFieldErrors(prev => ({ ...prev, ...errors }));
                    }
                  }
                  // Check password
                  const passwordValidationErrors = validatePassword(formData.password);
                  if (passwordValidationErrors.length > 0) {
                    setPasswordErrors(passwordValidationErrors);
                    setShowPasswordValidationPopup(true);
                    setFieldErrors(prev => ({
                      ...prev,
                      password: passwordValidationErrors.length === 5 ? 'Password is required' : 'Password does not meet requirements',
                    }));
                  }
                }
              }}
            >
            <button
              type="submit"
              disabled={isLoading || !isFormValidState || !formData.agreeToTerms}
              className="register-create-account-btn"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                'Create Account'
              )}
            </button>
            </div>
          </form>
        </div>
      </div>

      {/* Form Section for mobile and tablet */}
      <div className={`register-form-mb-tab-section ${isSmallScreen ? 'small-screen' : ''}`}>
        {/* Header for mobile and tablet */}
        <div
          className="register-form-mb-tab-section-header"
          onClick={handleFormRegisterClose}
        >
          <ArrowLeft className="h-5 w-5 text-[var(--color-text)]" />
          <h2 className="register-form-title">Create Your Account</h2>
        </div>

        {/* Form Section for mobile and tablet */}
        <form noValidate onSubmit={handleSubmit}>
          {/* Name Fields */}
          <div className="register-form-field">
            <label className="register-field-label">
              First Name <span className="text-red-500">*</span>
            </label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/solar_user-bold.svg`}
                alt="User"
                className="register-input-icon"
              />
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                maxLength={25}
                className={`register-input ${fieldErrors.firstName ? 'input-error-border' : ''}`}
                placeholder="Enter first name"
              />
            </div>
            {fieldErrors.firstName && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {fieldErrors.firstName}
              </p>
            )}
          </div>

          <div className="register-form-field">
            <label className="register-field-label">Last Name</label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/solar_user-bold.svg`}
                alt="User"
                className="register-input-icon"
              />
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                maxLength={25}
                className={`register-input ${fieldErrors.lastName ? 'input-error-border' : ''}`}
                placeholder="Enter last name"
              />
            </div>
            {fieldErrors.lastName && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {fieldErrors.lastName}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="register-form-field">
            <label className="register-field-label">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/Email.svg`}
                alt="Mail"
                className="register-input-icon"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`register-input ${fieldErrors.email ? 'input-error-border' : ''}`}
                placeholder="Enter your email"
                onKeyDown={(e) => {
                  if (e.key.length === 1 && !/[a-zA-Z0-9@._-]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            {fieldErrors.email && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {fieldErrors.email}
              </p>
            )}
            {!fieldErrors.email && emailSuggestion && (
              <p className="text-sm mt-1 text-gray-700">
                Did you mean{' '}
                <button
                  type="button"
                  onClick={acceptEmailSuggestion}
                  className="font-semibold underline text-[#22c55e] hover:opacity-80"
                >
                  {emailSuggestion}
                </button>
                ?
              </p>
            )}
          </div>
          {/* Mobile Number */}
          <div className="register-form-field">
            <label className="register-field-label" htmlFor="register-mobile-number-mobile">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <PhoneNumberInput
              inputId="register-mobile-number-mobile"
              inputName="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleMobileNumberChange}
              defaultCountry="ae"
              placeholder="Enter your mobile number"
              error={fieldErrors.mobileNumber}
            />
          </div>

          {/* Password */}
          <div className="register-form-field">
            <label className="register-field-label">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`}
                alt="Lock"
                className="register-input-icon"
              />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onBlur={() => {
                  // Hide popup when password field loses focus
                  setShowPasswordValidationPopup(false);
                }}
                className={`register-input ${fieldErrors.password ? 'input-error-border' : ''}`}
                placeholder="Create your password"
              />
              <button
                type="button"
                className="register-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0"/>
                  </svg>
                ) : (
                  <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0"/>
                  </svg>
                )}
              </button>
            </div>
            <div className="password-validation-popup-container">
              <PasswordValidationPopup
                isVisible={showPasswordValidationPopup}
                errors={passwordErrors}
                // password={formData.password}
                onClose={() => setShowPasswordValidationPopup(false)}
              />
            </div>
            {fieldErrors.password && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {fieldErrors.password}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="register-form-field">
            <label className="register-field-label">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`}
                alt="Lock"
                className="register-input-icon"
              />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`register-input ${fieldErrors.confirmPassword ? 'input-error-border' : ''}`}
                placeholder="Confirm your password"
              />
              <button
                type="button"
                className="register-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0"/>
                  </svg>
                ) : (
                  <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0"/>
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {fieldErrors.confirmPassword}
              </p>
            )}
          </div>

          {/* Terms and Privacy Policy */}
          <div className="register-terms-section">
            <div
              className={`register-terms-container ${fieldErrors.agreeToTerms && 'input-error-border'}`}
            >
              <label className="register-terms-checkbox-wrapper">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className="register-terms-checkbox"
                />
                <span className='checkmark'></span>
                <span className="register-terms-text">
                  I agree to the{' '}
                  <Link 
                    to="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="register-terms-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link 
                    to="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="register-terms-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </Link>
                </span>
              </label>
            </div>
            {fieldErrors.agreeToTerms && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {fieldErrors.agreeToTerms}
              </p>
            )}
          </div>

          <div className="sticky bottom-0 z-50 bg-[var(--modal-background)] pt-2">
            <div
              onClick={async (e) => {
                // If form is invalid, show errors
                if (!isFormValidState || !formData.agreeToTerms) {
                  e.preventDefault();
                  e.stopPropagation();
                  // Trigger validation to show all errors
                  try {
                    await registerSchema.validate(formData, { abortEarly: false });
                  } catch (validationError) {
                    if (validationError instanceof yup.ValidationError) {
                      const errors: {[key: string]: string} = {};
                      validationError.inner.forEach((error) => {
                        if (error.path) {
                          errors[error.path] = error.message;
                        }
                      });
                      setFieldErrors(prev => ({ ...prev, ...errors }));
                    }
                  }
                  // Check password
                  const passwordValidationErrors = validatePassword(formData.password);
                  if (passwordValidationErrors.length > 0) {
                    setPasswordErrors(passwordValidationErrors);
                    setShowPasswordValidationPopup(true);
                    setFieldErrors(prev => ({
                      ...prev,
                      password: passwordValidationErrors.length === 5 ? 'Password is required' : 'Password does not meet requirements',
                    }));
                  }
                }
              }}
            >
            <button
              type="submit"
              disabled={isLoading || !isFormValidState || !formData.agreeToTerms}
              className="register-create-account-btn"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                'Create Account'
              )}
            </button>
            </div>

            <div className="register-footer p-4">
              <p className="register-footer-text">
                Already have an account?{' '}
                <Link to="/login" className="register-footer-link">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </form>

      </div>

      {/* Footer */}
      {/* <div className="register-footer">
        <p className="register-footer-text">
          Already have an account?{' '}
          <Link to="/login" className="register-footer-link">
            Sign In
          </Link>
        </p>
      </div> */}

      {/* Modals */}
      <CaptchaModal
        isOpen={showCaptcha}
        onClose={handleCaptchaClose}
        onVerify={handleCaptchaVerify}
        onCaptchaGenerated={setCurrentCaptchaCode}
        showError={captchaError}
        onErrorClose={handleCaptchaErrorClose}
      />

      <CaptchaStatusModal
        isOpen={showCaptchaStatus}
        status={captchaStatus}
        message={captchaStatusMessage}
        onClose={() => setShowCaptchaStatus(false)}
        actionText={captchaStatus === 'failed' ? 'Try Again' : undefined}
        onAction={
          captchaStatus === 'failed'
            ? () => {
                setShowCaptchaStatus(false);
                setShowCaptcha(true);
              }
            : undefined
        }
      />

      <SuccessModal
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        title={successMessage}
        subtitle={successAction === 'Proceed to Login' ? undefined : successAction}
        status={successStatus}
        actionText={
          successAction === 'Proceed to Login' ? 'Proceed to Login' : undefined
        }
        onAction={
          successAction === 'Proceed to Login' ? handleSuccessAction : undefined
        }
      />

      <MobileAuthModal
        isOpen={showMobileAuth}
        onClose={handleMobileAuthClose}
        onSendOTP={handleSendOTP}
        onVerifyOTP={handleVerifyOTP}
      />

      {/* Apple login with firstName and lastname */}
      {/* <AppleNameModal
        isOpen={showAppleNameModal}
        onClose={() => setShowAppleNameModal(false)}
        onSubmit={(firstName, lastName) => {handleAppleLogin(firstName, lastName)}}
      /> */}

      {/* OAuth in-flight overlay — lets the user cancel if the popup was closed */}
      <OAuthLoadingOverlay
        isOpen={oauthInProgress !== null}
        provider={oauthInProgress ?? ''}
        onCancel={handleCancelOAuth}
      />

    </div>
  );
};

export default RegisterPage; 